/* eslint-disable jsx-a11y/click-events-have-key-events */
import * as React from "react";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { ActiveTabEnum, AudioEnum, CreateTicketCodes } from "../../../enums";
import {
  setCredit,
  setIsBettingEnabled,
  setWin,
} from "../../../store/config-slice";

import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setCarouselNumber } from "../../../store/picks/carousel-slice";
import {
  setActiveTab,
  setDrawTab,
  setSpinAgainTimeout,
} from "../../../store/picks/tabs-slice";
import {
  clearAllUserPicks,
  setShouldShowYourPick,
} from "../../../store/picks/userPick-slice";
import store from "../../../store/store";
import {
  setLastWinningNumber,
  setWinningNumber,
} from "../../../store/wheel-slice";
import { createTicket } from "../../../utils/TicketUtils";
import Countdown from "../../BettingPanel/CountdownOverlay/Countdown";
import ModalMessage from "../../Common/ModalMessage/ModalMessage";
import { playAudioEffect } from "../../../store/audio-slice";

const Spinner: React.FC = () => {
  let spinnerText = "";
  let spinnerClass = "";
  const drawStarted = useAppSelector((state) => state.tab.drawStarted);
  const pulsatingSpinnerTimeout =
    useAppSelector((state) => state.config.pulsatingSpinnerTimeout) / 2;
  const spinAgainTimeout = useAppSelector(
    (state) => state.tab.spinAgainTimeout
  );
  const [spinTimeout, setSpinTimeout] = useState(-1);
  const [pulse, setPulse] = useState(false);
  const [fade, setFade] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState("");
  const [shouldStartDraw, setShouldStartDraw] = useState(false);
  const isConnectionLost = useAppSelector(
    (state) => state.config.isConnectionLost
  );
  const isBettingEnabled = useAppSelector(
    (state) => state.config.isBettingEnabled
  );
  const [errorBeforePlay, setErrorBeforePlay] = useState(false);
  const [betDisabledBeforePlay, setBetDisabledBeforePlay] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const createTicketHandler = async (): Promise<void> => {
    const createTicketResponse = await createTicket();
    if (createTicketResponse.successCode === CreateTicketCodes.SUCCESS) {
      dispatch(setCredit({ balance: createTicketResponse.balance }));
      dispatch(setWinningNumber(createTicketResponse.drawnNumber));
      dispatch(setCarouselNumber(createTicketResponse.multiplier));
      if (
        createTicketResponse.payOutSum &&
        createTicketResponse.payOutSum > 0
      ) {
        dispatch(setWin(createTicketResponse.payOutSum));
      }
      dispatch(setDrawTab(true));
    } else if (
      createTicketResponse.successCode === CreateTicketCodes.OUT_OF_MONEY
    ) {
      setInfoModalMessage("message.outOfMoney");
    } else if (createTicketResponse.successCode === CreateTicketCodes.ERROR) {
      setInfoModalMessage("message.genericError");
      setErrorBeforePlay(true);
    } else if (
      createTicketResponse.successCode === CreateTicketCodes.BETTING_DISABLED
    ) {
      dispatch(setIsBettingEnabled(false));
      setBetDisabledBeforePlay(true);
    } else if (
      createTicketResponse.successCode === CreateTicketCodes.MAX_STAKE_REACHED
    ) {
      setInfoModalMessage("message.maxStakeReached");
    }
  };

  const handleSpinnerClick = (): void => {
    if (!store.getState().picks.shouldShowYourPick) {
      dispatch(setActiveTab(ActiveTabEnum.PLAY));
    } else if (
      store.getState().picks.pickedStakes.length > 0 &&
      !drawStarted &&
      store.getState().picks.shouldShowYourPick
    ) {
      setShouldStartDraw(true);
      setSpinTimeout(-1);
      dispatch(setSpinAgainTimeout(-1));
    }
  };

  useEffect(() => {
    if (betDisabledBeforePlay && isBettingEnabled) {
      dispatch(setSpinAgainTimeout(3));
      setBetDisabledBeforePlay(false);
    }
  }, [betDisabledBeforePlay, isBettingEnabled, dispatch]);

  useEffect(() => {
    if (errorBeforePlay && !isConnectionLost) {
      dispatch(setSpinAgainTimeout(3));
      setErrorBeforePlay(false);
    }
  }, [errorBeforePlay, isConnectionLost, dispatch]);

  useEffect(() => {
    if (drawStarted) {
      setPulse(true);
      setFade(false);
      const timeout = setTimeout(() => {
        setPulse(false);
        setFade(true);
        return () => clearTimeout(timeout);
      }, pulsatingSpinnerTimeout);
    }
  }, [drawStarted, pulsatingSpinnerTimeout]);

  useEffect(() => {
    setSpinTimeout(spinAgainTimeout);
  }, [spinAgainTimeout]);

  useEffect(
    () => () => {
      dispatch(setSpinAgainTimeout(-1));
      dispatch(setLastWinningNumber());
    },
    [dispatch]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (spinTimeout > 0) {
      timeout = setTimeout(() => {
        setSpinTimeout((prevValue) => prevValue - 1);
      }, 1000);
    } else if (spinTimeout === 0) {
      if (store.getState().picks.shouldShowYourPick) {
        dispatch(clearAllUserPicks());
      }
      dispatch(setShouldShowYourPick(false));
      dispatch(setSpinAgainTimeout(-1));
      dispatch(setActiveTab(ActiveTabEnum.PLAY));
      dispatch(setLastWinningNumber());
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [dispatch, spinTimeout]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (fade) {
      timeout = setTimeout(() => {
        setFade(false);
      }, 1000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [fade]);

  if (spinTimeout >= 0) {
    spinnerText = `${t("panel.drawingPanel.spinAgain")} ${spinTimeout}`;
  } else if (pulse) {
    spinnerClass = "pulse";
    spinnerText = t("panel.drawingPanel.goodLuck");
  } else if (fade) {
    spinnerClass = "fade_text";
    spinnerText = t("panel.drawingPanel.goodLuck");
  } else if (!store.getState().picks.shouldShowYourPick) {
    spinnerClass = "fade_text_infinite";
    spinnerText = t("panel.drawingPanel.placeBet");
  } else if (!fade) {
    if (shouldStartDraw || drawStarted) {
      spinnerText = t("panel.drawingPanel.draw");
    } else {
      spinnerText = t("panel.drawingPanel.spinAgain");
    }
  }

  return (
    <>
      <div
        role="button"
        tabIndex={-1}
        className={`spin_box 
          ${spinnerClass}
          ${shouldStartDraw || drawStarted ? "disabled" : ""}`}
        id="spin_box"
        onClick={() => {
          handleSpinnerClick();
          dispatch(playAudioEffect(AudioEnum.EFFECT_MODAL_OK));
        }}
      >
        <div className="spin_box_border">
          <div className="spin_box_content">
            <div className="spin_box_shadow">
              <div className="arrow_top" />
              <div className="text_cont">
                <p className="spin_text">{spinnerText}</p>
              </div>
              <div className="arrow_bottom" />
            </div>
          </div>
        </div>
      </div>
      {shouldStartDraw && (
        <Countdown
          cancelDrawStart={setShouldStartDraw}
          createTicket={createTicketHandler}
        />
      )}
      {infoModalMessage !== "" && (
        <ModalMessage
          message={infoModalMessage}
          onApprove={() => setInfoModalMessage("")}
        />
      )}
    </>
  );
};

export default Spinner;
